/**
 * src/features/billing/ui/IntervalToggle.tsx
 *
 * Segmented control for choosing the billing interval (monthly / annual).
 * Shared by PaywallPage and UpgradePromptModal — both are checkout entry points
 * that POST /api/billing/checkout with the selected interval (08-02). The server
 * maps the interval to STRIPE_PRICE_ID_MONTHLY / STRIPE_PRICE_ID_ANNUAL.
 *
 * a11y: implements the WAI-ARIA radiogroup pattern — role="radiogroup" wrapping
 * role="radio" segments with aria-checked, roving tabindex (only the checked
 * segment is tabbable), and Arrow/Home/End keyboard navigation.
 *
 * motion: the active segment is marked by a single shared-layout pill
 * (framer-motion layoutId) that slides between segments; instant under
 * prefers-reduced-motion.
 */

import { FC, KeyboardEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

export type BillingInterval = 'monthly' | 'annual';

export interface IntervalToggleProps {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  disabled?: boolean;
}

export const IntervalToggle: FC<IntervalToggleProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const options: { key: BillingInterval; label: string }[] = [
    { key: 'monthly', label: t('billing.interval_monthly', 'Monthly') },
    { key: 'annual', label: t('billing.interval_annual', 'Annual') },
  ];

  // WAI-ARIA radiogroup keyboard nav: Arrow keys move selection AND focus together
  // (roving tabindex), Home/End jump to the ends. Selection follows focus.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const currentIndex = options.findIndex((o) => o.key === value);
    let nextIndex = currentIndex;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + options.length) % options.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = options.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    onChange(options[nextIndex].key);
    btnRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('billing.interval_label', 'Billing interval')}
      onKeyDown={handleKeyDown}
      className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800"
    >
      {options.map((o, i) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            ref={(el) => {
              btnRefs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(o.key)}
            className={`relative flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-100 dark:focus-visible:ring-offset-slate-800 ${
              active
                ? 'text-slate-900 dark:text-slate-100'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {/* Shared-layout pill: slides between the two segments as selection changes. */}
            {active && (
              <motion.span
                layoutId="interval-pill"
                aria-hidden="true"
                className="absolute inset-0 rounded-lg bg-white dark:bg-slate-700 shadow-sm"
                transition={reduced ? { duration: 0 } : { duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              />
            )}
            <span className="relative z-10">{o.label}</span>
            {o.key === 'annual' && (
              <span className="relative z-10 text-micro text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40 px-1.5 py-0.5 rounded-full">
                {t('billing.annual_badge', '−15%')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
